import api from './api';
import {
  LD_JSON_ACCEPT,
  normalizeCollection,
  type CollectionResponse,
  type PagedCollection,
} from './hydraCollection';
import type {
  EmissionReportData,
  EmissionReportImport,
  EmissionReportImportStatus,
  EmissionReportImportWrite,
  EmissionReportMassFlow,
} from '../types/entities';

// Phase 35 (D-01 ff.): CRUD-Client fuer den Emissionsbericht-Import. Muster von plantKilnsApi.ts
// uebernommen -- mit EINEM bewussten Unterschied (D-17): getAll normalisiert NICHT auf ein reines
// Array, sondern gibt die vollstaendige HydraCollection zurueck, weil 'hydra:totalItems' fuer die
// Seitenzahl der paginierten Liste gebraucht wird.
export const BASE_PATH = '/api/emission_report_imports';

export interface EmissionReportImportFilters {
  page?: number;
  status?: EmissionReportImportStatus;
  year?: number;
}

// Exportiert (Plan 35-09): `emissionReportsFilterUrl.test.ts` belegt damit, dass die Jahreswahl
// der Importliste einen SERVERSEITIGEN Zeitraum auf `measurementRangeStart` erzeugt -- gegen die
// echte Umrechnung statt gegen eine zweite Nachbildung im Test.
export function buildQuery(filters: EmissionReportImportFilters): string {
  const params = new URLSearchParams();
  if (filters.page !== undefined) {
    params.set('page', String(filters.page));
  }
  if (filters.status !== undefined) {
    params.set('status', filters.status);
  }
  // Serverseitiger Jahresfilter ueber den DateFilter auf measurementRangeStart (Entity-Attribut,
  // Plan 35-02) -- kein Nachfiltern einer geladenen Seite im Browser (Projektregel).
  if (filters.year !== undefined) {
    params.set('measurementRangeStart[after]', `${filters.year}-01-01`);
    params.set('measurementRangeStart[before]', `${filters.year}-12-31`);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export const emissionReportImportsApi = {
  getAll: async (
    filters: EmissionReportImportFilters = {}
  ): Promise<PagedCollection<EmissionReportImport>> => {
    // D-17: hier bewusst NICHT wie plantKilnsApi.getAll auf ein Array normalisieren -- die
    // Gesamtzahl traegt die Pagination der Liste.
    //
    // Korrektur (Plan 35-10): dafuer MUSS `Accept: application/ld+json` mitgehen. Der zentrale
    // Axios-Client fordert `application/json` an, und in diesem Format liefert API Platform 4
    // eine nackte Liste ohne jede Gesamtzahl -- die Seite haette nie eine Zeile und nie eine
    // Seitenzahl gesehen. Gegen den laufenden Server geprueft.
    //
    // Punkt 11 (IN-07, 35.1-06): `normalizeCollection` liefert zusaetzlich `lastPage` aus
    // `view.last` -- die Seitenzahl der Liste kommt damit vom Server statt aus einer kopierten
    // Seitengroesse (`emissionReportsFilterUrl.pageCount`).
    const response = await api.get<CollectionResponse<EmissionReportImport>>(
      `${BASE_PATH}${buildQuery(filters)}`,
      LD_JSON_ACCEPT
    );
    return normalizeCollection(response.data);
  },

  getOne: async (id: number): Promise<EmissionReportImport> => {
    const response = await api.get<EmissionReportImport>(`${BASE_PATH}/${id}`);
    return response.data;
  },

  update: async (
    id: number,
    // Lesen liefert eingebettete Objekte, Schreiben verlangt IRIs -- deshalb ein eigener
    // Schreibtyp statt eines `Pick` auf der Lesegestalt.
    data: Partial<EmissionReportImportWrite>
  ): Promise<EmissionReportImport> => {
    const response = await api.patch<EmissionReportImport>(`${BASE_PATH}/${id}`, data, {
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(`${BASE_PATH}/${id}`);
  },

  // Multipart-Upload, kein normales `create()` -- der zentrale Axios-Client setzt global
  // 'Content-Type: application/json' (api.ts:5-10); ohne die Ueberschreibung fehlt dem Request
  // die Multipart-Grenze und das Backend meldet "Keine Datei uebermittelt", obwohl der
  // Netzwerk-Tab die Datei zeigt.
  upload: async (file: File): Promise<EmissionReportImport> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<EmissionReportImport>(`${BASE_PATH}/upload`, formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },

  // Punkt 6 (35.2-05): Speichern und Sperren in einem Request. Ohne `resultData` bleibt der Aufruf
  // wie bisher ohne Rumpf (Backend verwendet dann den zuletzt gespeicherten Stand, Plan 35.2-03).
  accept: async (id: number, resultData?: EmissionReportData): Promise<EmissionReportImport> => {
    const response =
      resultData !== undefined
        ? await api.post<EmissionReportImport>(`${BASE_PATH}/${id}/accept`, { resultData })
        : await api.post<EmissionReportImport>(`${BASE_PATH}/${id}/accept`);
    return response.data;
  },

  // Phase 40 (Plan 40-05/40-08): live gerechnete Mittel der Massenstroeme fuer die offene
  // Vorschau -- zustandsloser Endpunkt, rechnet ausschliesslich mit `EmissionReportMassFlowAverager`
  // (derselbe Dienst wie beim Uebernehmen), speichert nichts. Body und Antwort tragen denselben
  // `massFlows`-Abschnitt wie `resultData` (siehe `EmissionReportData.massFlows`).
  massFlowAverages: async (
    massFlows: Record<string, EmissionReportMassFlow> | []
  ): Promise<Record<string, EmissionReportMassFlow> | []> => {
    const response = await api.post<{ massFlows: Record<string, EmissionReportMassFlow> | [] }>(
      `${BASE_PATH}/mass_flow_averages`,
      { massFlows }
    );
    return response.data.massFlows;
  },
};
